import { Box, Stack, useTheme } from "@mui/material";
import { doughnutCard } from "@foodpilot/foods";
import type { MainScore } from "src/api/client-api/olapScores/olapScoresSchema";
import type { PackagingPreviewScores } from "src/api/client-api/packagings/useCalculatePackagingPreview";
import type { Packaging } from "src/api/client-api/packagings/useGetOnePackaging";
import { ScoreSidebarShell } from "src/components/Score/ScoreSidebarShell";
import { OlapScoresService } from "src/context/OlapScores/OlapScoresService";
import { useEntityScores } from "src/context/Scores/useEntityScores";
import { ScoreSelectHeader } from "src/pages/Product/v2/ProductFormV2/ScoreLiveSidebar/ScoreSelectHeader";
import { TotalScoreCard } from "src/pages/Product/v2/ProductFormV2/ScoreLiveSidebar/TotalScoreCard";

type PackagingScorePreviewCardProps = {
    appearance?: "legacy" | "product-v2";
    packaging?: Packaging;
    previewScores?: PackagingPreviewScores;
    isPreviewLoading?: boolean;
    isNarrow?: boolean;
    stickyTop?: number;
};

export const PackagingScorePreviewCard = ({
    appearance = "legacy",
    packaging,
    previewScores,
    isPreviewLoading = false,
    isNarrow = false,
    stickyTop = 0,
}: PackagingScorePreviewCardProps) => {
    const theme = useTheme();
    const { lastSelectedVariation, selectedScorePrecision } = useEntityScores();
    const selectedScoreId = lastSelectedVariation.scoreId;
    const persistedMainScore = packaging?.olapScores[selectedScoreId.toString()];
    // A variation-aware persisted request changes both the score value and its
    // unit. Never pair a response for another variation with the selected unit.
    const persistedScoreValue = OlapScoresService.normalizeNumericalScore(
        persistedMainScore?.unit === lastSelectedVariation.unit ? persistedMainScore.score.value : null,
        null,
    );
    const previewMainScore = previewScores?.[selectedScoreId];
    const previewVariation = previewMainScore?.variations.find(
        (variation) => variation.id === lastSelectedVariation.variationId,
    );

    // Once the preview endpoint has answered, its selected score is authoritative.
    // In particular, an explicit null must render the empty state instead of a stale
    // persisted value from before the form was edited.
    const previewScoreValue =
        previewScores === undefined ? undefined
        : lastSelectedVariation.variationId === null ? (previewMainScore?.score.value ?? null)
        : (previewVariation?.score.value ?? null);
    const scoreValue = previewScoreValue === undefined ? persistedScoreValue : previewScoreValue;
    const mainScore: MainScore = {
        score: { value: scoreValue },
        unit: lastSelectedVariation.unit,
        label: lastSelectedVariation.group,
        type: lastSelectedVariation.type,
        displayMode: lastSelectedVariation.displayMode,
        defaultDisplay: true,
        dimensions: previewMainScore?.dimensions ?? [],
        variations: previewMainScore?.variations ?? [],
        subscores: previewMainScore?.subscores ?? {},
    };

    return (
        <Box
            data-testid="packaging-score-preview-aside"
            sx={
                appearance === "product-v2" ?
                    {
                        maxWidth: "100%",
                        minWidth: 0,
                        position: isNarrow ? "static" : "sticky",
                        top: isNarrow ? undefined : stickyTop,
                        width: isNarrow ? "100%" : doughnutCard.width,
                        "& > .MuiBox-root": {
                            minWidth: 0,
                            width: "100%",
                        },
                    }
                :   { position: { lg: "sticky" }, top: { lg: theme.spacing(3) } }
            }
        >
            <ScoreSidebarShell>
                <ScoreSelectHeader />
                <Stack sx={{ padding: "16px", gap: "16px" }}>
                    <TotalScoreCard
                        mainScore={mainScore}
                        unit={lastSelectedVariation.unit}
                        precision={selectedScorePrecision}
                        referenceScore={undefined}
                        referenceName={undefined}
                        improvement={lastSelectedVariation.improvement}
                        weight={{ captionWeightG: null, unit: null }}
                        isLoading={isPreviewLoading}
                    />
                </Stack>
            </ScoreSidebarShell>
        </Box>
    );
};
